/**
 * Carbon Intensity Overlay Component
 *
 * Displays real-time carbon intensity data as a floating overlay
 * Can be toggled on/off across all dashboards
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  Co2 as CarbonIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
  TrendingUp as HighIcon,
  TrendingDown as LowIcon,
  TrendingFlat as ModerateIcon,
  Park as EcoIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import carbonIntensityApi from '../../services/carbonIntensityApi';

// Carbon intensity thresholds and colors based on UK Carbon Intensity API
const getIntensityLevel = (intensity) => {
  if (intensity <= 50) return { level: 'Very Low', color: '#00ff88', icon: EcoIcon };
  if (intensity <= 100) return { level: 'Low', color: '#9ACD32', icon: LowIcon };
  if (intensity <= 200) return { level: 'Moderate', color: '#f1c40f', icon: ModerateIcon };
  if (intensity <= 300) return { level: 'High', color: '#ff9800', icon: HighIcon };
  return { level: 'Very High', color: '#e74c3c', icon: FactoryIcon };
};

export default function CarbonOverlay({ visible = true, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carbonData, setCarbonData] = useState(null);
  const [generationMix, setGenerationMix] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchCarbonData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch current intensity and generation mix in parallel
      const [intensityResult, mixResult] = await Promise.all([
        carbonIntensityApi.getCurrentIntensity(),
        carbonIntensityApi.getCurrentGenerationMix(),
      ]);

      if (intensityResult && intensityResult.length > 0) {
        setCarbonData(intensityResult[0]);
      }

      if (mixResult && mixResult.generationmix) {
        setGenerationMix(mixResult.generationmix);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching carbon data:', err);
      setError('Failed to load carbon data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (visible) {
      fetchCarbonData();
      // Auto-refresh every 5 minutes
      const interval = setInterval(fetchCarbonData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [visible, fetchCarbonData]);

  if (!visible) return null;

  const intensity = carbonData?.intensity?.actual || carbonData?.intensity?.forecast || 0;
  const intensityInfo = getIntensityLevel(intensity);
  const IntensityIcon = intensityInfo.icon;

  // Get top fuel sources
  const topFuels = [...generationMix]
    .sort((a, b) => b.perc - a.perc)
    .slice(0, 4);

  return (
    <Fade in={visible}>
      <Card
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: expanded ? 320 : 220,
          zIndex: 1200,
          backgroundColor: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${intensityInfo.color}40`,
          borderRadius: 3,
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${intensityInfo.color}20`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Tooltip
              title="Shows how much CO₂ is emitted for each unit of electricity generated right now. Lower = greener grid. Updates every 30 minutes from the UK Carbon Intensity API."
              arrow
              componentsProps={{
                tooltip: { sx: { backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', maxWidth: 280, p: 1.5 } },
                arrow: { sx: { color: '#1a1a2e' } },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                <Box
                  sx={{
                    backgroundColor: `${intensityInfo.color}20`,
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CarbonIcon sx={{ color: intensityInfo.color, fontSize: 20 }} />
                </Box>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                  Carbon Intensity
                </Typography>
              </Box>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip
                title="Refresh carbon data"
                arrow
                componentsProps={{
                  tooltip: { sx: { backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)' } },
                  arrow: { sx: { color: '#1a1a2e' } },
                }}
              >
                <IconButton
                  size="small"
                  onClick={fetchCarbonData}
                  disabled={loading}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={expanded ? "Collapse panel" : "Expand to see generation mix breakdown"}
                arrow
                componentsProps={{
                  tooltip: { sx: { backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)' } },
                  arrow: { sx: { color: '#1a1a2e' } },
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
                >
                  {expanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              {onClose && (
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#e74c3c' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Main Content */}
          {loading && !carbonData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#00d4ff' }} />
            </Box>
          ) : error ? (
            <Typography sx={{ color: '#e74c3c', fontSize: '0.8rem', textAlign: 'center' }}>
              {error}
            </Typography>
          ) : (
            <>
              {/* Intensity Value */}
              <Box
                sx={{
                  textAlign: 'center',
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: `${intensityInfo.color}15`,
                  border: `1px solid ${intensityInfo.color}30`,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <IntensityIcon sx={{ color: intensityInfo.color, fontSize: 24 }} />
                  <Typography
                    sx={{
                      color: intensityInfo.color,
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      lineHeight: 1,
                    }}
                  >
                    {intensity}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', alignSelf: 'flex-end', mb: 0.5 }}>
                    gCO2/kWh
                  </Typography>
                </Box>
                <Chip
                  label={intensityInfo.level}
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: `${intensityInfo.color}30`,
                    color: intensityInfo.color,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                  }}
                />
              </Box>

              {/* Expanded View */}
              <Collapse in={expanded}>
                <Box sx={{ mt: 2 }}>
                  {/* Generation Mix */}
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      mb: 1,
                    }}
                  >
                    Current Generation Mix
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {topFuels.map((fuel) => {
                      const fuelColors = {
                        gas: '#ff9800',
                        coal: '#795548',
                        nuclear: '#9c27b0',
                        wind: '#00bcd4',
                        solar: '#f1c40f',
                        hydro: '#3498db',
                        biomass: '#8bc34a',
                        imports: '#607d8b',
                        other: '#9e9e9e',
                      };
                      const color = fuelColors[fuel.fuel] || '#9e9e9e';
                      return (
                        <Box key={fuel.fuel}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', textTransform: 'capitalize' }}>
                              {fuel.fuel}
                            </Typography>
                            <Typography sx={{ color, fontWeight: 600, fontSize: '0.75rem' }}>
                              {fuel.perc.toFixed(1)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={fuel.perc}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: color,
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Time info */}
                  {carbonData && (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textAlign: 'center' }}>
                        Settlement Period: {carbonData.from?.slice(11, 16)} - {carbonData.to?.slice(11, 16)}
                      </Typography>
                      {lastUpdated && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', textAlign: 'center', mt: 0.5 }}>
                          Updated: {lastUpdated.toLocaleTimeString()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
}
